import type { Node, Gender, RelType } from 'relatives-tree/lib/types';
import { green, orange } from '@mui/material/colors';
import { Person } from '../types/tree';

function setToArray<T>(set?: Set<T>): T[] {
  const result: T[] = [];
  if (set) {
    set.forEach((item) => {
      result.push(item);
    });
  }
  return result;
}

export function transformNodeData(data: Person[]): Node[] {
  const validPeople = (data || []).filter((p): p is Person =>
    Boolean(p && p.id),
  );
  const knownIds = new Set(validPeople.map((p) => p.id));

  const parentsMap = new Map<string, Set<string>>();
  const childrenMap = new Map<string, Set<string>>();
  const spousesMap = new Map<string, Set<string>>();
  const siblingsMap = new Map<string, Set<string>>();

  for (const person of validPeople) {
    parentsMap.set(person.id, new Set<string>());
    childrenMap.set(person.id, new Set<string>());
    spousesMap.set(person.id, new Set<string>());
    siblingsMap.set(person.id, new Set<string>());
  }

  // Populate initial links, filtering references to missing IDs and self
  for (const person of validPeople) {
    const id = person.id;

    if (
      person.father_id &&
      knownIds.has(person.father_id) &&
      person.father_id !== id
    ) {
      parentsMap.get(id)!.add(person.father_id);
      childrenMap.get(person.father_id)!.add(id);
    }

    if (
      person.mother_id &&
      knownIds.has(person.mother_id) &&
      person.mother_id !== id
    ) {
      parentsMap.get(id)!.add(person.mother_id);
      childrenMap.get(person.mother_id)!.add(id);
    }

    if (Array.isArray(person.children_id)) {
      for (const cid of person.children_id) {
        if (cid && knownIds.has(cid) && cid !== id) {
          childrenMap.get(id)!.add(cid);
          parentsMap.get(cid)!.add(id);
        }
      }
    }

    if (Array.isArray(person.spouse_id)) {
      for (const sid of person.spouse_id) {
        if (sid && knownIds.has(sid) && sid !== id) {
          spousesMap.get(id)!.add(sid);
          spousesMap.get(sid)!.add(id);
        }
      }
    }

    if (Array.isArray(person.sibling_id)) {
      for (const sibId of person.sibling_id) {
        if (sibId && knownIds.has(sibId) && sibId !== id) {
          siblingsMap.get(id)!.add(sibId);
          siblingsMap.get(sibId)!.add(id);
        }
      }
    }
  }

  // Derive sibling links from shared parents
  childrenMap.forEach((children) => {
    const childrenList = setToArray(children);
    for (let i = 0; i < childrenList.length; i++) {
      for (let j = i + 1; j < childrenList.length; j++) {
        const c1 = childrenList[i];
        const c2 = childrenList[j];
        if (c1 !== c2) {
          siblingsMap.get(c1)?.add(c2);
          siblingsMap.get(c2)?.add(c1);
        }
      }
    }
  });

  // Ensure no self-references exist
  validPeople.forEach((person) => {
    parentsMap.get(person.id)?.delete(person.id);
    childrenMap.get(person.id)?.delete(person.id);
    spousesMap.get(person.id)?.delete(person.id);
    siblingsMap.get(person.id)?.delete(person.id);
  });

  return validPeople.map((person: Person) => {
    const id = person.id;
    const gender = (person.gender === 'female' ? 'female' : 'male') as Gender;

    return {
      id,
      gender,
      spouses: setToArray(spousesMap.get(id)).map((spouse) => ({
        id: spouse,
        type: 'marriage' as RelType,
      })),
      siblings: setToArray(siblingsMap.get(id)).map((sibling) => ({
        id: sibling,
        type: 'blood' as RelType,
      })),
      parents: setToArray(parentsMap.get(id)).map((parent) => ({
        id: parent,
        type: 'blood' as RelType,
      })),
      children: setToArray(childrenMap.get(id)).map((child) => ({
        id: child,
        type: 'blood' as RelType,
      })),
    };
  });
}

export function stringAvatar(data: any) {
  let children: string = '';
  if (data?.first_name && data?.last_name) {
    const newName = `${data.first_name} ${data.last_name}`;
    children = `${newName.split(' ')[0][0]}${newName.split(' ')[1][0]}`;
  }
  return {
    sx: {
      bgcolor: data?.gender === 'male' ? green[800] : orange[500],
      width: 100,
      height: 100,
      border: '2px solid black',
    },
    children: children,
  };
}
